import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useNavigate  } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Select from "react-select";
import { Tooltip } from 'react-tooltip';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from 'jspdf';
import debounce from "lodash.debounce";
import contractService from '../../services/ContractService';
import userService from '../../services/UserService';
import Nav from '../../components/contract/NavContract';
import LayerPdf from '../../components/contract/LayerPdf';
import SlidePreviewPdf from '../../components/contract/SlidePreviewPdf';
import Upload from '../../components/contract/Upload';
import "../../static/css/datatable.css";

const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
    }).format(value);
};
const  statusType = [
  { label: 'In lavorazione', value: 'In lavorazione' },
  { label: 'Inviato', value: 'Inviato' },
  { label: 'Accettato', value: 'Accettato' },
  { label: 'Rifiutato', value: 'Rifiutato' },
  { label: 'Revisionato', value: 'Revisionato' }
];
const monthOptions = [
    { value: 1, label: "Gennaio" },
    { value: 2, label: "Febbraio" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Aprile" },
    { value: 5, label: "Maggio" },
    { value: 6, label: "Giugno" },
    { value: 7, label: "Luglio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Settembre" },
    { value: 10, label: "Ottobre" },
    { value: 11, label: "Novembre" },
    { value: 12, label: "Dicembre" }
];

export default function Contract({ itemsPerPage = 25 }) {
    const defaultValues = {
        months: [],
        status: [{ label: 'In lavorazione', value: 'In lavorazione' },{ label: 'Inviato', value: 'Inviato' }],
        commercial: null,
        designer: null,
        types: []
    };
    const comingValues = {
        coming: true,
        months: [],
        status: [{ label: 'In lavorazione', value: 'In lavorazione' },{ label: 'Inviato', value: 'Inviato' },{ label: 'Accettato', value: 'Accettato' }],
        commercial: null,
        designer: null,
        types: []
    };
    const resetAllValues = {
        months: [],
        status: [],
        commercial: null,
        designer: null,
        types: []
    };
    const { register, control, watch, handleSubmit, reset, getValues } = useForm({
        defaultValues
    });
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPageLocal, setItemsPerPageLocal] = useState(itemsPerPage);
    const [isLayerPdfOpen, setIsLayerPdfOpen] = useState(false);
    const [isSlidePrevOpen, setIsSlidePrevOpen] = useState(false);
    const [isSlideDocOpen, setIsSlideDocOpen] = useState(false);
    const [openAction, setOpenAction] = useState(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [contract, setContract] = useState(null);
    const [contractPdf, setContractPdf] = useState(null);
    const [contractPrev, setContractPrev] = useState(null);
    const [type, setType] = useState({});
    const [commercials, setCommercials] = useState({});
    const [designers, setDesigners] = useState({});
    const [contractType, setContractType] = useState({});

    const navigate = useNavigate();
    const handleClickAdd = () => {
        navigate('/contract-add');
    };

    const searchValue = watch("search", "");
    const searchFields = ["clientName", "eventName","number"];
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const [openFilter, setOpenFilter] = useState(null);

    const totalPages = Math.ceil(filteredData.length / itemsPerPageLocal);
    const startIndex = (currentPage - 1) * itemsPerPageLocal;
    const endIndex = startIndex + itemsPerPageLocal;
    const currentItems = filteredData.slice(startIndex, endIndex);
    // Funzione di navigazione
    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const normalizePayload = (formData) => {
        return {
            months: formData.months?.map(m => m.value) || [],
            status: formData.status?.map(s => s.value) || [],
            idComm: formData.commercial ? Number(formData.commercial.id_user) : null,
            idDesi: formData.designer ? Number(formData.designer.id_user) : null,
            types: formData.types?.map(s => s.id_type) || [],
            coming: formData.coming ? true : false
        };
    };
    const onReset = () => {
        reset(resetAllValues);
        fetchContract(getValues());
    };
    const onComing = () => {
        setSortConfig({ key: null, direction: null });
        reset(comingValues);
        fetchContract(getValues());
    };
    const onDefault = () => {
        reset(defaultValues);
        fetchContract(getValues());
    };
    const handleFilterClick = (index) => {
        setOpenFilter(openFilter === index ? null : index);
    };
    const fetchContractFilter = (data) => {
        fetchContract(data);
        setOpenFilter(false);
    };
    const fetchUsers= async () => {
        try {
            const res = await userService.getAllActive();
            const resultsData = res.data || [];
            const commercialsData = [];
            const designersData = [];

            resultsData.forEach(user => {
              if (user?.role === 'ROLE_COMM') commercialsData.push(user);
              if (user?.role === 'ROLE_DESIGN') designersData.push(user);
            });

            setCommercials(commercialsData);
            setDesigners(designersData);
        } catch (error) {
            console.error('Error fetching users', error);
        }
    };
    const fetchContType= async () => {
        try {
            const res = await contractService.getAllType();
            const resultsData = res.data || [];
            setContractType(resultsData);
        } catch (error) {
            console.error('Error fetching contract type', error);
        }
    };
    const fetchContract= async (data) => {
        try {
            const payload = normalizePayload(data);
            const res = await contractService.getFilter(payload);
            const resultsData = res.data || [];
            const total = res.data.length || 0;

            setResults(resultsData); // Salva i risultati
            setFilteredData(resultsData);
            setTotalResults(total); // Salva il totale dei risultati
            setCurrentPage(1);
            setLoading(false);
            // Calcoliamo dinamicamente itemsPerPageLocal dopo aver ricevuto i dati
            const defaultItemsPerPage = Math.min(itemsPerPage, total);
            setItemsPerPageLocal(defaultItemsPerPage);

        } catch (error) {
            console.error('Error fetching clients', error);
        }
    };

    useEffect(() => {
        fetchContract(getValues());
        fetchUsers();
        fetchContType();
    }, []);

    const filterData = useCallback((term) => {
        if (!term || term.trim() === "") {
            const sorted = applySort(results, sortConfig);
            setFilteredData(sorted);
            return;
        }
        const lower = term.toLowerCase();

        const filtered = results.filter(item =>
            searchFields.some(field => {
                let value = item[field];

                if (!value) return false;

                // campo number formattato
                if (field === "number") {
                    value = value.toString().padStart(3, "0");
                }

                return value.toString().toLowerCase().includes(lower);
            })
        );

        // applica ordinamento anche ai risultati filtrati
        const sorted = applySort(filtered, sortConfig);

        setFilteredData(sorted);
        setCurrentPage(1);
    }, [results, sortConfig, searchFields]);

    const applySort = (data, sortCfg) => {
        const { key, direction } = sortCfg;

        if (!key || !direction) return data;

        return [...data].sort((a, b) => {
            let valA = a[key];
            let valB = b[key];

            if (key === "amount" || key === "mq") {
                valA = isNaN(valA) ? -Infinity : Number(valA);
                valB = isNaN(valB) ? -Infinity : Number(valB);
            } else if (key === "dueDate" || key === "startDate") {
                valA = valA ? new Date(valA).getTime() : -Infinity;
                valB = valB ? new Date(valB).getTime() : -Infinity;
            }  else {
                valA = valA?.toString().toLowerCase() || "";
                valB = valB?.toString().toLowerCase() || "";
            }

            if (valA < valB) return direction === "asc" ? -1 : 1;
            if (valA > valB) return direction === "asc" ? 1 : -1;
            return 0;
        });
    };
    const sortData = (key) => {
        let direction = "asc";
        if (sortConfig.key === key) {
            if (sortConfig.direction === "asc") direction = "desc";
            else if (sortConfig.direction === "desc") direction = null; // reset
        }
        const newSort = { key, direction };
        setSortConfig(newSort);

        // reset → torna tutti i dati filtrati senza sorting
        if (!direction) {
            filterData(searchValue);   // lascia filtro attivo ma senza ordinamento
            return;
        }
        const sorted = applySort(filteredData, newSort);
        setFilteredData(sorted);
    };

    const debouncedFilter = useMemo(
        () => debounce((term) => filterData(term), 300),
        [filterData]     // dipende SOLO da filterData
    );

    useEffect(() => {
        debouncedFilter(searchValue);
        return () => debouncedFilter.cancel();
    }, [searchValue, debouncedFilter]);

    useEffect(() => { //Quando results cambia per filtri applicati ricalcola la lista corretta:
        const sorted = applySort(results, sortConfig);
        setFilteredData(sorted);
    }, [results]);

    useEffect(() => { //Effetto che aggiorna i totali
        const total = filteredData.length;
        setTotalResults(total);

        if (total > 0) {
            setItemsPerPageLocal(Math.min(itemsPerPage, total));
        }
    }, [filteredData]);

    const toggleLayerPdf = (contract,type) => {
        setContractPdf(contract);
        setType(type);
        setIsLayerPdfOpen(!isLayerPdfOpen);
    };
    const toggleSlidePreview = (contract) => {
        setContractPrev(contract);
        setIsSlidePrevOpen(!isSlidePrevOpen);
    };
    const toggleSlideUp = (contract) => {
        setContract(contract);
        setIsSlideDocOpen(!isSlideDocOpen);
    };
    // Funzione per aggiornare il numero di elementi per pagina
    const handleItemsPerPageChange = (event) => {
        setItemsPerPageLocal(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };
    // Funzione per generare le opzioni dinamiche per la select
    const generateItemsPerPageOptions = () => {
        const options = [];
        const maxItems = totalResults;

        if (maxItems <= 2) {
          options.push(2);
        } else if (20 <= maxItems && maxItems <= 50){
          options.push(10, 25, maxItems);
        } else if (maxItems > 50) {
            options.push(10, 25, 50);
        } else {
            // Aggiungiamo anche multipli di 2 tra il minimo e il massimo
            for (let i = 2; i <= maxItems; i *= 2) {
                if (i < maxItems) options.push(i);
            }
            // Se il numero totale è abbastanza grande, aggiungiamo anche il numero massimo
            if (maxItems > 2) options.push(maxItems);
        }

        return options;
    };

    // Funzione per gestire l'apertura e la posizione del sottomenu azione
    const handleActionClick = (index,e) => {
        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;
        const adjustedX = offsetX - 80;
        const adjustedY = offsetY - 10;

        setPosition((prevPositions) => ({
              ...prevPositions,
              [index]: { x: adjustedX, y: adjustedY }, // Imposta la posizione dell'elemento
            }));
        setOpenAction(openAction === index ? null : index);
    };
    const getFormattedDate = () => {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}-${month}-${year}`;
    };
    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Contratti");

        worksheet.columns = [
            { header: "Stato", key: "status", width: 15 },
            { header: "NR-Data", key: "number", width: 15 },
            { header: "Cliente", key: "client", width: 28 },
            { header: "Evento", key: "event", width: 15 },
            { header: "Tipologia", key: "type", width: 15 },
            { header: "Scadenza", key: "dateOffer", width: 15 },
            { header: "Importo (€)", key: "amount", width: 16 },
            { header: "MQ", key: "mq", width: 15 },
            { header: "Data Consegna", key: "dateSupply", width: 28 },
            { header: "Posizione", key: "position", width: 15 },
            { header: "Note", key: "note", width: 30 }
        ];

        worksheet.getRow(1).font = { bold: true };
        worksheet.autoFilter = "A1:H1";
        worksheet.views = [{ state: "frozen", ySplit: 1 }];

        filteredData.forEach((contract) => {
            const row = worksheet.addRow({
                status: contract.status,
                number: contract.number.toString().padStart(3, "0") + " del " + format(new Date(contract.startDate), 'dd/MM/yyyy', { locale: it }),
                client: contract.clientName,
                event: contract.eventName + " - " + contract.eventYear,
                type: contract.contractType,
                dateOffer:contract.dateOffer ? new Date(contract.dateOffer) : "",
                amount: contract.amount,
                mq:contract.mq,
                dateSupply:contract.dateSupply ? format(new Date(contract.dateSupply), 'dd/MM/yyyy HH:mm', { locale: it }) : "",
                position:contract.position,
                note: contract.note || ""
            });
            row.getCell("amount").numFmt = '€ #,##0.00';
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            `contratti_${getFormattedDate()}.xlsx`
        );
    };
    const handleExportPdf = () => {
        const doc = new jsPDF('l', 'mm', 'a4');//configuro con l(stampa orizzontale)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.text("Lista Contratti", 14, 10); //Titolo
        doc.autoTable({
            head: [['NR','Evento','Contatti','Montaggio','Smontaggio','Vettore','Note']], // Intestazioni della tabella
            body: results.map((event, index) => [index + 1,`${event.name}\n${event.year}\n${event.address}\n${event.city}\n${event.startDate} ${event.endDate}`,
                `Telefono: ${event.phone}\nEmail: ${event.email}`,
                `Inizio: ${event.startAssembly}\nFine ${event.endAssembly}`,
                `Inizio: ${event.startDisassembly}\nFine: ${event.endDisassembly}`,
                `Nome: ${event.vector.name}\nIndirizzo: ${event.vector.address}`,
                event.note]), // Corpo della tabella
            startY: 20, // Imposta la posizione di inizio della tabella
            theme: 'grid', // Aggiungi griglia alla tabella
        });
        doc.save(`event_${getFormattedDate()}.pdf`);
    };

  	return (
  	<>
  	    <div className="row">
            <div className="col-md-12">
                {/* Nav Contract */}
                <Nav />
            </div>
        </div>
        <div className="row text-nowrap">
            <div className="col-md-12 mb-6">
                <div className="card card-action">
                    <div className="card-header align-items-center">
                        <h5 onClick={() => handleFilterClick(0)} className={`card-action-title mb-0 cursor-pointer ${openFilter === 0 ? "show" : ""}`} >
                            <i className="bx bx-slider me-2 bx-sm"></i>Filtri
                        </h5>
                        <div className="card-action-element">
                            <button type="button" className="btn btn-primary btn-sm edit-address me-3" onClick={onDefault}>Default</button>
                            <button type="button" className="btn btn-label-secondary btn-sm edit-address me-3" onClick={onComing}>Futuri</button>
                            <button type="button" className="btn btn-label-danger btn-sm edit-address" onClick={onReset}>Reset</button>
                        </div>
                    </div>
                    <div className={`offcanvas offcanvas-end ${openFilter === 0 ? "show" : ""}`} >
                        <div className="offcanvas-header border-bottom">
                            <h5 className="offcanvas-title">Filtri disponibili</h5>
                            <button onClick={() => handleFilterClick(0)} className="btn-close text-reset"></button>
                        </div>
                        <div className="offcanvas-body mx-0 flex-grow-0 p-6 h-100">
                            <form className="add-new-user pt-0" onSubmit={handleSubmit(fetchContractFilter)}>
                                <div className="row">
                                    <div className="col mb-6">
                                        <label className="form-label">Mese</label>
                                            <Controller
                                            name="months"
                                            control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        placeholder="Seleziona"
                                                        isMulti
                                                        classNamePrefix='filter'
                                                        options={monthOptions}
                                                    />
                                                )}
                                            />
                                    </div>
                                    <div className="col mb-6">
                                        <label className="form-label">Stato</label>
                                            <Controller
                                            name="status"
                                            control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        isMulti
                                                        placeholder="Seleziona"
                                                        classNamePrefix='filter'
                                                        options={statusType}
                                                    />
                                                )}
                                            />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col mb-6">
                                        <label className="form-label">Commerciale</label>
                                            <Controller
                                            name="commercial"
                                            control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        isClearable
                                                        placeholder="Seleziona un commerciale"
                                                        classNamePrefix='filter'
                                                        options={commercials}
                                                        getOptionLabel={(e) => `${e.name}`}
                                                        getOptionValue={(e) => e.id_user}
                                                        onChange={(selected) => field.onChange(selected)}
                                                    />
                                                )}
                                            />
                                    </div>
                                    <div className="col mb-6">
                                        <label className="form-label">Tipologia</label>
                                            <Controller
                                            name="types"
                                            control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        isMulti
                                                        placeholder="Seleziona"
                                                        classNamePrefix='filter'
                                                        options={contractType}
                                                        getOptionLabel={(e) => e.name}
                                                        getOptionValue={(e) => e.id_type}
                                                    />
                                                )}
                                            />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6 mb-6">
                                        <label className="form-label">Progettista</label>
                                            <Controller
                                            name="designer"
                                            control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        isClearable
                                                        placeholder="Seleziona un progettista"
                                                        classNamePrefix='filter'
                                                        options={designers}
                                                        getOptionLabel={(e) => `${e.name}`}
                                                        getOptionValue={(e) => e.id_user}
                                                        onChange={(selected) => field.onChange(selected)}
                                                    />
                                                )}
                                            />
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-primary me-3">Filtra</button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
  	    <div className="card">
            {/* List Eventi */}
            <div className="card-datatable table-responsive">
                <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                    <div className="row">
                        <div className="col-md-2">
                            <div className="dataTables_length">
                                <label>
                                    <select name="DataTables_Table_0_length" className="form-select" id="itemsPerPage"
                                        value={itemsPerPageLocal} onChange={handleItemsPerPageChange}>
                                        {generateItemsPerPageOptions().map((value) => (
                                            <option key={value} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                        <div className="col-md-10">
                            <div className="dt-action-buttons text-xl-end text-lg-start text-md-end text-start d-flex align-items-center justify-content-end flex-md-row flex-column mb-6 mb-md-0 mt-n6 mt-md-0">
                                <div className="dataTables_filter">
                                    <label>
                                        <input className="form-control" type="text"
                                            placeholder="Cerca per ..."
                                            {...register('search')}
                                        />
                                    </label>
                                </div>
                                <div className="dt-buttons btn-group flex-wrap">
                                    <div className="btn-group">
                                        <button onClick={handleExportExcel} className="btn buttons-collection dropdown-toggle btn-label-secondary mx-4" type="button" >
                                            <span><i className="bx bx-export me-2 bx-sm"></i>Export</span>
                                        </button>
                                    </div>
                                    <button className="btn add-new btn-primary" onClick={handleClickAdd}>
                                         <span>
                                            <i className="bx bx-plus bx-sm me-0 me-sm-2"></i>
                                            <span className="d-none d-sm-inline-block">Aggiungi</span>
                                         </span>
                                   </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <table className="datatables-users table border-top dataTable no-footer dtr-column">
                        <thead>
                            <tr>
                                <th>NOTE</th>
                                <th>STATO</th>
                                <th className="cursor-pointer" onClick={() => sortData("startDate")}>
                                    NR - DATA {sortConfig.key === "startDate" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th className="cursor-pointer"  onClick={() => sortData("clientName")}>
                                    CLIENTE {sortConfig.key === "clientName" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th className="cursor-pointer" onClick={() => sortData("eventName")}>
                                    EVENTO {sortConfig.key === "eventName" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th>TIPOLOGIA</th>
                                <th>SCADENZA</th>
                                <th className="cursor-pointer"  onClick={() => sortData("amount")}>
                                    IMPORTO {sortConfig.key === "amount" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th className="cursor-pointer"  onClick={() => sortData("mq")}>
                                    MQ {sortConfig.key === "mq" && (
                                            <span>{sortConfig.direction === "asc" ? "▲" : ""} {sortConfig.direction === "desc" ? "▼" : ""}</span>
                                    )}
                                </th>
                                <th>AZIONI</th>
                            </tr>
                        </thead>
                        {loading && (
                            <tbody>
                                <tr className="odd">
                                    <td className="dataTables_empty">Loading...</td>
                                </tr>
                            </tbody>
                        )}
                        <tbody>
                             {currentItems.map((result,index) => (
                                <tr key={result.id_contract}>
                                    <td data-tooltip-id={`tooltip-note-${result.id_contract}`}
                                        data-tooltip-content={`${result.note}`}>
                                        <i className={`bx bxs-circle bx-xs ${result.note != "" ? "text-info" : ""}`}></i>
                                    </td>
                                    <td>{result.status}</td>
                                    <td>{result.number.toString().padStart(3, "0")} - {result.startDate ? format(new Date(result.startDate), 'dd/MM/yyyy', { locale: it }) : ''}</td>
                                    <td>
                                        <NavLink to={`/client-detail/${result.id_client}`}>
                                            {result.clientName}
                                        </NavLink>
                                    </td>
                                    <td>
                                    {(result.eventName)
                                    && (
                                    <>
                                       <NavLink to={`/event-detail/${result.id_event}`}>
                                           {result.eventName}
                                       </NavLink>
                                    </>
                                    )}
                                    </td>
                                    <td>{result.contractType}</td>
                                    <td>{result.dateOffer ? format(new Date(result.dateOffer), 'dd/MM/yyyy', { locale: it }) : ''}</td>
                                    <td>{formatCurrency(result.amount)}</td>
                                    <td>{result.mq}</td>
                                    <td>
                                        <div className="dropdown">
                                            <button type="button" className="btn p-0" onClick={(e) => handleActionClick(index,e)}>
                                                <i className="bx bx-dots-vertical-rounded"></i>
                                            </button>
                                            <div className={`dropdown-menu ${openAction === index ? "show-action" : ""}`}
                                                style={{
                                                    position: 'absolute',
                                                    transform: position[index]
                                                        ? `translate3d(${position[index].x}px, ${position[index].y}px, 0px)`
                                                        : 'translate3d(0px, 0px, 0px)',
                                                    transition: 'transform 0.3s ease',
                                                }}
                                            >
                                                {(result.status === "Revisionato" || result.status === "Accettato" || result.status === "Rifiutato")
                                                && (
                                                <>
                                                    <a className="dropdown-item" onClick={() => toggleLayerPdf(result,"CONTRATTO")}>
                                                        <i className="bx bx-show me-1"></i>Contratto
                                                    </a>
                                                </>
                                                )}
                                                <a className="dropdown-item" onClick={() => toggleLayerPdf(result,"PROGETTO")}>
                                                    <i className="bx bx-show me-1"></i>Progetto
                                                </a>
                                                {/*{(result.status === "In lavorazione" || result.status === "Accettato" || result.status === "Inviato")
                                                && (
                                                <>*/}
                                                    <a className="dropdown-item" onClick={() => toggleSlideUp(result)}>
                                                        <i className="bx bx-cloud-upload me-1"></i>Upload
                                                    </a>
                                               {/* </>
                                                )}*/}
                                                {(result.status != "Accettato")
                                                && (
                                                <>
                                                    <a className="dropdown-item" onClick={() => toggleSlidePreview(result)}>
                                                        <i className="bx bx-show me-1"></i>Preview
                                                    </a>
                                                </>
                                                )}
                                                {/*anche la modifica andrebbe abilitata solo per 3 stati come Upload*/}
                                                <NavLink to={`/contract-edit/${result.id_contract}`} className="dropdown-item">
                                                    <i className="bx bx-edit-alt me-1"></i>Modifica
                                                </NavLink>
                                                <NavLink to={`/contract-duplicate/${result.id_contract}`} className="dropdown-item">
                                                    <i className="bx bx-duplicate me-1"></i>Duplica
                                                </NavLink>
                                            </div>
                                        </div>
                                    </td>
                                    <Tooltip id={`tooltip-note-${result.id_contract}`} place="right" className="tooltip-custom" />
                                </tr>
                              ))}
                         </tbody>
                    </table>
                    <div className="row">
                        <div className="col-sm-12 col-md-6">
                            <div className="dataTables_info" role="status" aria-live="polite">
                                Risultati ottenuti : {totalResults}
                            </div>
                        </div>
                        <div className="col-sm-12 col-md-6">
                            <div className="dataTables_paginate paging_simple_numbers custom-ryo">
                                {!loading && (
                                    <div className="pagination">
                                        <button className="page-link" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                                            <i className="bx bx-chevron-left bx-18px"></i>
                                        </button>
                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <button
                                              key={index + 1}
                                              onClick={() => goToPage(index + 1)}
                                              disabled={currentPage === index + 1}
                                              className={`page-link ${currentPage === index + 1  ? "current-page" : ""}`}
                                            >
                                              {index + 1}
                                            </button>
                                        ))}
                                        <button className="page-link" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                                            <i className="bx bx-chevron-right bx-18px"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Layer */}
            { contractPdf && (
            <LayerPdf show={isLayerPdfOpen} onClose={toggleLayerPdf}  contract={contractPdf} type={type}/>
                )
            }
            { contractPrev && (
                <SlidePreviewPdf show={isSlidePrevOpen} onClose={toggleSlidePreview}  contract={contractPrev}/>
                )
            }
            { contract && (
                <Upload show={isSlideDocOpen} onClose={toggleSlideUp}  contract={contract}/>
                )
            }
            {/* Layer */}
            {/* /List Eventi*/}
  	    </div>
    </>
	);
}